import React, { useEffect, useState } from 'react'

import { LogsIcon } from 'lucide-react'

import { Slider, Stack, Typography, Grid, Tooltip, useTheme, alpha, Grow } from '@mui/material'
import type { SliderValueLabelProps } from '@mui/material/Slider'

import { FilterField } from './FilterField'
interface SliderFilterProps {
  onChange?: () => void
  column?: object
  rows?: [], 
  filters?: null
}

export const SliderFilter = (props: SliderFilterProps) => {
  const { 
    column,
    rows = [],
    onChange = () => {}, 
    filters = [],
  } = props

  const theme = useTheme()

  /**
   * The column's value for a row — through its `valueGetter` when it has one.
   *
   * ⚠️ THIS USED TO READ `row[column.field]` ONLY, which silently deleted the whole field
   * for any DERIVED column. A column whose value is computed rather than stored — a count,
   * a status, anything living inside a jsonb blob — has no `row[field]` at all, so every
   * row scored `NaN`, `nums` came out empty, `min`/`max` both fell to 0, and the
   * `min >= max` guard below returned `null`. The filter did not misbehave; it did not
   * EXIST, with nothing anywhere saying so.
   *
   * Found on `/internal/signups` (VRG-545), where eleven numeric columns read out of
   * `roi_submission` and not one of them appeared in the drawer. `RolesPage`'s member-count
   * column is in the same position.
   *
   * Backwards compatible by construction: a column with no `valueGetter` resolves exactly
   * as before. The signature is MUI X v7's `(value, row, column, apiRef)`, matching
   * `getExportSortValue` in `Table.tsx`, which already had to solve this for CSV export —
   * the grid's own sort and filter engines read the same function, so the slider's range now
   * describes the numbers the reader can actually see.
   */
  const valueOfRow = (row: any) => {
    const raw = row?.[column.field]
    const getter = (column as any)?.valueGetter
    if (typeof getter !== 'function') return raw
    try {
      return getter(raw, row, column, undefined)
    } catch {
      // A getter that throws on a partial row must not take the whole filter down with it.
      return raw
    }
  }

  // Filter to finite numbers first — prevents NaN when values are null/undefined
  // (e.g. dimension columns where not all products have metafields set).
  const nums = rows.map(row => Number(valueOfRow(row))).filter(v => Number.isFinite(v))
  const min = nums.length ? Math.min(...nums) : 0
  const max = nums.length ? Math.max(...nums) : 0

  // Optional value formatter from the column (e.g. a currency formatter for a price
  // column), used for the thumb label + the min/max end labels so the slider reads as
  // "$1.55" instead of a raw "1.54775".
  const fmt = (v: number) => {
    const f = (column as any)?.formatValue
    return f ? f(v) : v
  }

  const [value, setValue] = useState<number[]>([min, max]);

  const calculateRange = (filters) => {
    let selectedMin = min
    let selectedMax = max
    // Callers pass the column's filters double-wrapped (`[columnFilters]`), so flatten
    // before reading operators — otherwise `filter.operator` is read off an array, never
    // matches, and the range always collapses back to [min, max] (the thumb bounce bug).
    ;(filters ?? []).flat().forEach((filter) => {
      if (!filter) return
      if (filter.operator == '>=' && filter.value != null) {
        selectedMin = Number(filter.value)
      }
      if (filter.operator == '<=' && filter.value != null) {
        selectedMax = Number(filter.value)
      }
    })
    return [selectedMin, selectedMax]
  }

  // Sync the thumbs to the committed filter range. Depend on the resolved lo/hi VALUES
  // (not the `filters` array, which is a new reference every parent render) so a spurious
  // re-render mid-drag can't yank the thumbs back to the committed range (the bounce).
  const committed = calculateRange(filters)
  useEffect(() => {
    setValue([committed[0], committed[1]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committed[0], committed[1]])

  const handleChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number[]);
  };

  // only triggered on mouse up release of the slider thumbs and use thumb index logic to apply filters one at a time
  const handleChangeCommited = (e) => {
    if (e.target.dataset.index == '0') {
      onChange({'field': column?.field, 'operator': '>=', 'value': value[0] != min ? value[0] : null})
    }
    if (e.target.dataset.index == '1') {
      onChange({'field': column?.field, 'operator': '<=', 'value': value[1] != max ? value[1] : null})
    }
  };

  // custom tooltip 
  function ValueLabelComponent(props: SliderValueLabelProps) {
    const { children, value } = props;
    return (
      <Tooltip 
        enterTouchDelay={0}
        placement="bottom"
        title={fmt(value)}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, .5],
                },
              },
            ],
          },
        }}>
        {children}
      </Tooltip>
    );
  }

  const handleClear = () => {
    setValue([min, max])
  }

  // Nothing to filter → don't render the field at all (was showing a dead "NaN / NaN"
  // or "0 / 0" slider): NaN = the column has no numeric data, ±Infinity = no rows, and
  // min === max = every row shares one value (e.g. all 0), so there's no range to drag.
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
    return null
  }

  return (
    <FilterField
      label={<>{column?.headerName} <Typography variant="body3" color="text.secondary">{column?.secondaryText}</Typography></>}
      showClear={JSON.stringify(value) != JSON.stringify([min, max])}
      onClear={handleClear}
    >
        <Stack px={2} pt={4}>
          <Slider
            value={value}
            min={min}
            max={max}
            // Step from the column (e.g. 0.01 for a price so it slides by the cent instead
            // of snapping to whole dollars). Falls back to a smooth ~1/200 of the range so a
            // decimal column never defaults to integer-only steps.
            step={(column as any)?.step ?? Math.min(1, (max - min) / 200 || 1)}
            // Don't let the two thumbs swap/cross — dragging the max thumb left past the
            // min thumb otherwise disturbs the min ("messing up the left ball"). With this,
            // the dragged thumb just stops at the other.
            disableSwap
            onChange={handleChange}
            valueLabelDisplay="auto"
            onChangeCommitted={handleChangeCommited}
            slots={{
              valueLabel: ValueLabelComponent
            }}
            sx={{
              // White (theme text) circles; the filled track a softer, semi-transparent
              // white so the thumbs are the emphasis, not the bar (was black thumbs on a
              // bright white bar).
              color: theme.palette.text.primary,
              '& .MuiSlider-thumb': {
                backgroundColor: theme.palette.text.primary,
              },
              '& .MuiSlider-track': {
                border: 'none',
                backgroundColor: alpha(theme.palette.text.primary, 0.4),
              },
              '& .MuiSlider-rail': {
                opacity: .15,
              },
            }}
          />
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body3" color="text.secondary" justifyContent="left" >
            {fmt(min)}
          </Typography>
          <Typography variant="body3" color="text.secondary" justifyContent="right">
            {fmt(max)}
          </Typography>
        </Stack>
    </FilterField>
  )
}